import { useState } from "react";
import { HelpCircle, Mail, Plus, ArrowRight } from "lucide-react";
import { useMarketConfig, price as fmtPrice } from "@/utils/marketConfig";
import "@/styles/landing.css";

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(0);
  const { cfg: mkt, ready: mktReady } = useMarketConfig();
  const p = (amt) => (mktReady ? fmtPrice(mkt, amt) : "…"); // never render a possibly-wrong currency

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "What is PlacementOS?",
          a: "PlacementOS is an AI-powered career platform that helps job seekers optimize their resumes for ATS (Applicant Tracking Systems), build personalized skill roadmaps, and connect with relevant job opportunities. We combine artificial intelligence with career expertise to maximize your employability."
        },
        {
          q: "How does the resume analysis work?",
          a: "Our AI analyzes your resume against industry standards and ATS requirements. It evaluates formatting, keyword density, action verbs, quantified achievements, and section structure. You receive a detailed score breakdown with specific recommendations for improvement."
        },
        {
          q: "Is my data secure?",
          a: "Yes, we take data security seriously. All data is encrypted in transit (TLS 1.3) and at rest (AES-256). We never sell your personal information or share your resume without your explicit consent. See our Privacy Policy for details."
        }
      ]
    },
    {
      category: "Full Career OS",
      questions: [
        {
          q: "What is the Full Career OS?",
          a: "The Full Career OS is our premium subscription that unlocks all PlacementOS features including: unlimited resume analyses, AI-powered resume rewriting, personalized skill roadmaps with course recommendations, AI career companion, job matching, and priority support."
        },
        {
          q: "How much does the Full Career OS cost?",
          a: `The Full Career OS costs ${p(mkt.prices.pass_annual)} for 1 year access. This is a one-time payment with no recurring fees. All updates and features during your subscription period are included.`
        },
        {
          q: "Can I get a refund?",
          a: "All purchases are final and non-refundable. By making a purchase, you acknowledge and agree to this no-refund policy. For exceptional circumstances, contact support@placementos.ai."
        }
      ]
    },
    {
      category: "Resume & Analysis",
      questions: [
        {
          q: "What file formats are supported?",
          a: "We support PDF and DOCX file formats. For best results, we recommend uploading a PDF version of your resume."
        },
        {
          q: "How accurate is the ATS score?",
          a: "Our ATS scoring is based on analysis of millions of job descriptions and ATS requirements. While no score can guarantee job placement, our analysis helps identify the key areas that most ATS systems evaluate."
        },
        {
          q: "Can I analyze multiple resumes?",
          a: "Full Career OS users can analyze up to 5 different resumes. This is useful if you're targeting different roles or industries and need tailored versions of your resume."
        },
        {
          q: "Will the AI-rewritten resume look like mine?",
          a: "Yes! Our AI enhances your existing content - it doesn't replace it. We preserve all your experiences, achievements, and personal information while improving formatting, action verbs, and ATS keywords."
        }
      ]
    },
    {
      category: "Skill Path & Learning",
      questions: [
        {
          q: "How are learning roadmaps personalized?",
          a: "Our AI considers your target role, current skills, and experience level to create a custom learning path. We recommend specific courses from leading platforms like Coursera, Udemy, and Google, organized into progressive phases."
        },
        {
          q: "Are the recommended courses free?",
          a: "We recommend a mix of free and paid courses. Many platforms offer financial aid or free audit options. The courses are curated based on quality and relevance, not cost."
        }
      ]
    },
    {
      category: "Technical & Support",
      questions: [
        {
          q: "I'm having trouble uploading my resume. What should I do?",
          a: "Ensure your file is in PDF or DOCX format and under 5MB. If issues persist, try a different browser or clear your cache. Contact support@placementos.ai if problems continue."
        },
        {
          q: "How do I delete my account and data?",
          a: "You can request account deletion by emailing privacy@placementos.ai. We'll process your request within 30 days and delete all associated data per our retention policy."
        },
        {
          q: "How can I contact support?",
          a: "Email us at support@placementos.ai. We respond within 24 hours on business days. For urgent issues, mention 'URGENT' in your subject line."
        }
      ]
    }
  ];

  return (
    <div className="ap-root" data-testid="faq-page">

      {/* ============ HERO ============ */}
      <section className="ap-mesh ap-grid-overlay relative pt-24 pb-10" data-testid="faq-hero-section" style={{ overflowX: "clip" }}>
        <div className="ap-section text-center max-w-[720px] mx-auto ap-rise">
          <span className="w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-6 ap-rise ap-rise-1" style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}>
            <HelpCircle className="w-8 h-8" />
          </span>
          <span className="ap-eyebrow ap-rise ap-rise-1">Help Center</span>
          <h1 className="ap-display font-extrabold mt-3 text-[var(--ap-ink)]" style={{ fontSize: "clamp(32px,5vw,56px)", lineHeight: 1.04, letterSpacing: "-0.02em" }}>
            Frequently asked <span className="ap-grad-text">questions</span>.
          </h1>
          <p className="text-[17px] md:text-[18px] text-[var(--ap-ink-2)] leading-relaxed mt-5 max-w-[560px] mx-auto ap-rise ap-rise-2">
            Find answers to common questions about PlacementOS.
          </p>
        </div>
      </section>

      {/* ============ FAQ CONTENT ============ */}
      <section className="ap-section py-10 max-w-[860px]" data-testid="faq-content-section">
        {faqs.map((category, catIndex) => {
          const offset = faqs.slice(0, catIndex).reduce((sum, c) => sum + c.questions.length, 0);
          return (
            <div key={catIndex} className="mb-12">
              <div className="flex items-center gap-3 mb-5">
                <h2 className="ap-display font-bold text-[20px] md:text-[22px] text-[var(--ap-ink)]">
                  {category.category}
                </h2>
                <span className="flex-1 ap-divider" />
              </div>
              <div className="space-y-3.5">
                {category.questions.map((item, qIndex) => {
                  const globalIndex = offset + qIndex;
                  const isOpen = openIndex === globalIndex;

                  return (
                    <div
                      key={qIndex}
                      className="ap-card overflow-hidden transition-shadow"
                      style={isOpen ? { borderColor: "var(--ap-violet)", boxShadow: "0 18px 50px -24px rgba(91,61,245,.4)" } : {}}
                    >
                      <button
                        onClick={() => setOpenIndex(isOpen ? -1 : globalIndex)}
                        className="group w-full flex items-center justify-between gap-4 text-left px-6 py-5 bg-transparent border-0 cursor-pointer"
                        data-testid={`faq-toggle-${globalIndex}`}
                      >
                        <span className={`ap-display font-bold text-[16.5px] transition-colors ${isOpen ? "text-[var(--ap-violet)]" : "text-[var(--ap-ink)] group-hover:text-[var(--ap-violet)]"}`}>
                          {item.q}
                        </span>
                        <span className="shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors"
                          style={{ background: isOpen ? "var(--ap-violet)" : "var(--ap-violet-soft)", color: isOpen ? "#fff" : "var(--ap-violet)" }}>
                          <Plus className={`w-4 h-4 transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`} />
                        </span>
                      </button>
                      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                        <div className="overflow-hidden">
                          <p className="px-6 pb-6 text-[15px] text-[var(--ap-muted)] leading-relaxed" data-testid={`faq-answer-${globalIndex}`}>
                            {item.a}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {/* ============ STILL HAVE QUESTIONS ============ */}
      <section className="ap-section pb-24">
        <div className="ap-card ap-mesh overflow-hidden text-center py-14 px-6 rounded-[28px]" data-testid="faq-contact-section">
          <span className="w-12 h-12 rounded-2xl grid place-items-center text-white mx-auto mb-5" style={{ background: "var(--ap-fire)" }}>
            <Mail className="w-6 h-6" />
          </span>
          <h3 className="ap-h2" style={{ fontSize: "clamp(24px,3vw,34px)" }}>Still have <span className="ap-grad-text">questions</span>?</h3>
          <p className="text-[15px] md:text-[16px] text-[var(--ap-muted)] mt-3 max-w-[440px] mx-auto">We're here to help. Reach out to our support team.</p>
          <a
            href="mailto:support@placementos.ai"
            className="ap-btn ap-btn-fire ap-btn-lg mt-7"
            data-testid="faq-contact-link"
          >
            <Mail className="w-[18px] h-[18px]" /> Contact Support <ArrowRight className="w-[18px] h-[18px]" />
          </a>
        </div>
      </section>
    </div>
  );
}
